import { useState } from "react";
import { get, isNil } from "lodash";
import PropTypes from "prop-types";
import {
  offScaleValues,
  scaleText,
  suggestedNeedsField,
} from "./recommendation-guide";
import { fillTemplate } from "./workshop-view";

const WorkshopNeedsCard = ({
  slot,
  savedIn,
  savedPath,
  needLike,
  needsFields,
  stepName,
  labels,
  saving,
  onUse,
  onScale,
}) => {
  const [scaleTried, setScaleTried] = useState(false);
  const [scaling, setScaling] = useState(false);
  const current = needsFields.find((field) => field.path === savedIn) || null;
  const scale = scaleText(current || needsFields[0]);

  if (!isNil(current)) {
    const applied = savedIn === savedPath;
    const off = applied ? offScaleValues(slot, current) : [];
    return (
      <div
        role="status"
        className={`workshop-needs workshop-needs--${off.length ? "warn" : "ok"}`}
      >
        <strong className="workshop-needs__title">{labels.LINKED}</strong>
        <span className="workshop-needs__line">
          {fillTemplate(labels.SCALE, { scale })}
        </span>
        {off.length ? (
          <span className="workshop-needs__line">
            {fillTemplate(labels.OFF_SCALE, {
              values: off.join(", "),
              scale,
            })}
          </span>
        ) : null}
        {off.length && !scaleTried ? (
          <button
            type="button"
            className="workshop-needs__action"
            disabled={saving || scaling}
            onClick={() => {
              setScaling(true);
              Promise.resolve()
                .then(() => onScale())
                .then((applied) => {
                  if (applied !== false) setScaleTried(true);
                })
                .catch(() => {})
                .then(() => setScaling(false));
            }}
          >
            {labels.SET_SCALE}
          </button>
        ) : null}
        {off.length && scaleTried ? (
          <span className="workshop-needs__line">
            {fillTemplate(labels.SCALE_BY_HAND, {
              values: get(current, "scale", [])
                .map((option) => option.value)
                .join(", "),
            })}
          </span>
        ) : null}
      </div>
    );
  }

  const suggestion = suggestedNeedsField({
    needsFields,
    slot,
    path: savedIn,
    stepName,
  });

  if (!isNil(suggestion)) {
    const off = offScaleValues(slot, suggestion);
    return (
      <div role="status" className="workshop-needs">
        <strong className="workshop-needs__title">
          {fillTemplate(labels.SUGGEST, { field: suggestion.label })}
        </strong>
        <span className="workshop-needs__line">
          {fillTemplate(labels.SCALE, { scale })}
        </span>
        {off.length ? (
          <span className="workshop-needs__line">
            {fillTemplate(labels.OFF_SCALE, { values: off.join(", "), scale })}
          </span>
        ) : null}
        <button
          type="button"
          className="workshop-needs__action"
          disabled={saving}
          onClick={() => onUse(suggestion.path)}
        >
          {labels.USE}
        </button>
      </div>
    );
  }

  if (!needLike) return null;

  if (!needsFields.length)
    return (
      <div role="status" className="workshop-needs workshop-needs--warn">
        <span className="workshop-needs__line">{labels.NONE}</span>
      </div>
    );

  return (
    <div role="status" className="workshop-needs">
      <strong className="workshop-needs__title">{labels.PICK}</strong>
      <div className="workshop-needs__choices">
        {needsFields.map((field) => (
          <button
            key={field.path}
            type="button"
            className="workshop-needs__choice"
            disabled={saving}
            onClick={() => onUse(field.path)}
          >
            {field.name}
          </button>
        ))}
      </div>
      <span className="workshop-needs__line">
        {fillTemplate(labels.SCALE, { scale })}
      </span>
    </div>
  );
};

WorkshopNeedsCard.propTypes = {
  slot: PropTypes.object.isRequired,
  savedIn: PropTypes.string,
  savedPath: PropTypes.string,
  needLike: PropTypes.bool,
  needsFields: PropTypes.arrayOf(PropTypes.object),
  stepName: PropTypes.string,
  labels: PropTypes.object.isRequired,
  saving: PropTypes.bool,
  onUse: PropTypes.func.isRequired,
  onScale: PropTypes.func.isRequired,
};

WorkshopNeedsCard.defaultProps = {
  savedIn: null,
  savedPath: null,
  needLike: false,
  needsFields: [],
  stepName: "",
  saving: false,
};

export default WorkshopNeedsCard;
